/**
 * One command per drag mechanic — and the reason there is a command layer at all.
 *
 * View ADR §12: **every mechanic has a keyboard path and a menu path, and the menu path "dispatches
 * the same command the drop does — it is not a reduced fallback."** A vocabulary is the only way to
 * make that a property rather than a promise: three entry points construct the same value, one
 * function decides what it means, one function applies it, and a test can enumerate the union and
 * assert that no member is pointer-only.
 *
 * ── The split: decide, then apply ────────────────────────────────────────────────────────────
 * `moveOutcome` is pure over a read-only draft and answers three questions at once — may this land
 * (the gate), would it change anything, and what does the trainer need to be told. `applyMove`
 * mutates. The store runs the first, returns early on a refusal or a no-op, and only then runs the
 * second.
 *
 * That order is what `aCancelledDragWritesNothingToTheStore` pins. A cancelled drag never builds a
 * command at all; a drag released over a refused target builds one and gets a `refused` outcome
 * before any producer runs. Either way `runOperation` is not reached, so there is no patch, no undo
 * entry and no dirty flag — the three things a trainer would otherwise have to undo after changing
 * their mind mid-gesture.
 *
 * ── The outcome carries FACTS, never words ───────────────────────────────────────────────────
 * "Przeniesiono na pozycję 3 z 5" is a sentence, and `model/` imports no i18next. So the outcome
 * hands back the position, the total and whether the slot was re-anchored, and the surface that owns
 * the live region words it with the `t` it already has. A `t` threaded down here would make the
 * entity untestable without a locale for no gain — the numbers are the part with a rule in them.
 *
 * ── Why the position is measured AFTER the move, not before ──────────────────────────────────
 * `moveOutcome` reports where the block will END UP, which is not always the index the drag
 * reported: a block moving DOWN inside its own day passes under the rows it vacated, so a drop
 * reported at gap 4 lands at position 3. Announcing the reported index would tell the trainer a
 * number they can then read off the screen as something else. The normalisation is one line and it
 * lives here, because it is the same line for the pointer, the keyboard and the menu.
 *
 * No React, no DOM, no query hook, no i18next: this is `model/`.
 */

import type { Draft } from 'immer';

import type { AuthorableExerciseRole, ExercisePick } from './draftFactory';
import {
  addExercise,
  insertExerciseAt,
  moveDayToPosition,
  moveDayToWeekday,
  moveExercise,
  moveExerciseBlockToWarmup,
  workBlockCount,
  workBlockOf,
} from './draftOperations';
import {
  exerciseDropRefusal,
  libraryDropRefusal,
  locateDaySite,
  locateExerciseDay,
  positionDropRefusal,
  weekdayDropRefusal,
  type DropRefusal,
} from './dropGates';
import type { PlanDraft } from './planDraft.types';

/**
 * The three mechanics, as data.
 *
 * The library insert is a command of its own rather than a fourth case of `moveExercise`, because
 * its source is not in the tree: nothing is removed, no slot travels, and its gate asks a different
 * question (does the target day have room, not does the source phase allow the lift).
 */
export type MoveCommand =
  | {
      readonly kind: 'moveDayToWeekday';
      readonly daySlotToken: string;
      /** 1 (Monday) – 7 (Sunday). */
      readonly targetWeekday: number;
    }
  | {
      /**
       * The tape's reorder. A separate command from `moveDayToWeekday` rather than a mode branch
       * inside it, because the two carry different coordinates and refuse for different reasons —
       * one names a weekday that a sequence plan has none of, the other names a position that a
       * weekly plan places nothing by. Folding them would make the coordinate's MEANING depend on a
       * field neither the caller nor the command carries.
       */
      readonly kind: 'moveDayToPosition';
      readonly daySlotToken: string;
      /** A 0-based position among the phase's days. */
      readonly targetIndex: number;
    }
  | {
      readonly kind: 'moveExercise';
      readonly exerciseSlotToken: string;
      readonly targetDaySlotToken: string;
      /** A position among the target day's main-work blocks; `blockCount` means "at the end". */
      readonly targetBlockIndex: number;
    }
  | {
      /**
       * A main-work block dropped on its own day's warm-up zone (owner, 29 October 2026). A role
       * change, so it carries no target day and no index: the warm-up run is the source day's, and it
       * draws no positions to land between, so the block appends to it.
       */
      readonly kind: 'moveExerciseToWarmup';
      readonly exerciseSlotToken: string;
    }
  | {
      readonly kind: 'insertFromLibrary';
      readonly targetDaySlotToken: string;
      /**
       * A position among the target day's main-work blocks, or `'end'` to APPEND.
       *
       * `'end'` exists for the library's PRESS path, which has no pointer to read a position from
       * and no reason to learn the day's block count in order to say "after the last one". A number
       * is still clamped, so the two agree wherever they overlap.
       */
      readonly targetBlockIndex: number | 'end';
      readonly pick: ExercisePick;
      /**
       * `Main` lands at the reported position. `Warmup` appends to the day's warm-up run, because
       * that block draws no positions to land between — see `dropGates`' note on why it is not a
       * drop target with an index.
       */
      readonly role: AuthorableExerciseRole;
    };

/** What a command would do, or why it cannot. The live region's whole input. */
export type MoveOutcome =
  | { readonly kind: 'refused'; readonly reason: DropRefusal }
  /** Legal, and changes nothing — a block dropped back where it was. No patch, no announcement. */
  | { readonly kind: 'unchanged' }
  | {
      readonly kind: 'movedDay';
      readonly weekday: number;
      /** The session name the target weekday was holding, when the drop is a swap. */
      readonly swappedWith: string | null;
    }
  | {
      /** The tape's reorder landed. 1-based, for reading aloud, exactly like `movedExercise`. */
      readonly kind: 'movedDayPosition';
      readonly position: number;
      readonly total: number;
    }
  | {
      readonly kind: 'movedExercise';
      /** 1-based, for reading aloud. */
      readonly position: number;
      readonly total: number;
      /** The block crossed into another day, so its slot was re-minted and its chain restarts. */
      readonly reAnchored: boolean;
    }
  | {
      /** The block now stands in the warm-up run. `count` is how many exercises it carried there. */
      readonly kind: 'movedToWarmup';
      readonly count: number;
    }
  | {
      readonly kind: 'inserted';
      readonly position: number;
      readonly total: number;
      /**
       * The slot token the insert minted, once it HAS been minted.
       *
       * Absent from `moveOutcome`, which is computed against the pre-move tree and cannot know a
       * token that does not exist yet; filled in by the store's `runMoveCommand` after the operation
       * has run. The one caller that needs it is a library standing OVER the day it edits — the
       * builder's drawer — which closes on a successful press and has to send focus to the card it
       * just made rather than leaving it on `<body>` inside the focus trap.
       *
       * It is `string`, never `string | null`: an insert that minted no token did not happen, and
       * `runMoveCommand` answers `refused` there rather than announcing one. Absent means "not
       * filled in yet" — the pre-move answer — and nothing else.
       */
      readonly exerciseSlotToken?: string;
    };

/**
 * Where a block lands, once the rows it vacated are taken out of the count.
 *
 * Only applies to a move inside one day: a block arriving from another day displaces nothing on the
 * way, so the reported gap IS the position.
 */
function landingPosition(from: number, to: number): number {
  return to > from ? to - 1 : to;
}

/**
 * What this command means against this tree — the gate, the no-op check and the announcement's
 * numbers, in one answer.
 *
 * Called twice per act and deliberately so: once while the pointer is over a target (to decide
 * whether an indicator is drawn at all) and once at the moment of dispatch. Both readings run
 * against the same draft, so they cannot disagree.
 */
export function moveOutcome(draft: PlanDraft, command: MoveCommand): MoveOutcome {
  if (command.kind === 'moveDayToWeekday') {
    const refusal = weekdayDropRefusal(draft, command.daySlotToken, command.targetWeekday);
    if (refusal !== null) return { kind: 'refused', reason: refusal };
    const site = locateDaySite(draft, command.daySlotToken);
    if (site === null) return { kind: 'refused', reason: 'unknownTarget' };
    if (site.day.dayOfWeek === command.targetWeekday) return { kind: 'unchanged' };
    const occupant = site.phase.days.find(
      (day) => day.slotToken !== command.daySlotToken && day.dayOfWeek === command.targetWeekday,
    );
    // A swap needs a weekday to hand back. Without one the occupant would leave the week entirely,
    // which `moveDayToWeekday` refuses — so this reports the refusal rather than promising a move.
    if (occupant !== undefined && site.day.dayOfWeek === null) {
      return { kind: 'refused', reason: 'unknownTarget' };
    }
    return {
      kind: 'movedDay',
      weekday: command.targetWeekday,
      swappedWith:
        occupant === undefined
          ? null
          : (occupant.sessionName?.trim() ?? occupant.name?.trim() ?? null) || null,
    };
  }

  if (command.kind === 'moveDayToPosition') {
    const refusal = positionDropRefusal(draft, command.daySlotToken, command.targetIndex);
    if (refusal !== null) return { kind: 'refused', reason: refusal };
    const site = locateDaySite(draft, command.daySlotToken);
    if (site === null) return { kind: 'refused', reason: 'unknownTarget' };
    const from = site.phase.days.findIndex((day) => day.slotToken === command.daySlotToken);
    if (from === command.targetIndex) return { kind: 'unchanged' };
    // The position is reported AFTER the move, like every other outcome here. A day moving DOWN the
    // tape passes under the slot it vacated, so the reported index IS where it lands — the splice
    // removes it before it re-inserts, which is the same normalisation `landingPosition` makes for a
    // gap-addressed exercise drop and the reason that helper is not reused: this command already
    // addresses a POSITION rather than a gap.
    return {
      kind: 'movedDayPosition',
      position: command.targetIndex + 1,
      total: site.phase.days.length,
    };
  }

  if (command.kind === 'moveExercise') {
    const source = locateExerciseDay(draft, command.exerciseSlotToken);
    if (source === null) return { kind: 'refused', reason: 'unknownTarget' };
    const block = workBlockOf(source.day, command.exerciseSlotToken);
    if (block === null) return { kind: 'refused', reason: 'unknownTarget' };

    const refusal = exerciseDropRefusal(
      draft,
      command.exerciseSlotToken,
      command.targetDaySlotToken,
      block.size,
    );
    if (refusal !== null) return { kind: 'refused', reason: refusal };

    const target = locateDaySite(draft, command.targetDaySlotToken);
    if (target === null) return { kind: 'refused', reason: 'unknownTarget' };
    const sameDay = target.day.slotToken === source.day.slotToken;
    const blockCount = workBlockCount(target.day);
    const to = Math.min(Math.max(command.targetBlockIndex, 0), blockCount);

    if (sameDay) {
      const landing = landingPosition(block.position, to);
      if (landing === block.position) return { kind: 'unchanged' };
      return {
        kind: 'movedExercise',
        position: landing + 1,
        total: blockCount,
        reAnchored: false,
      };
    }

    return {
      kind: 'movedExercise',
      position: to + 1,
      // The block is not in the target day yet, so it adds one position to the count it lands in.
      total: blockCount + 1,
      reAnchored: true,
    };
  }

  if (command.kind === 'moveExerciseToWarmup') {
    // A standalone warm-up has no block and is already where this would put it — the zone's own
    // rows are not liftable, so this is the stale-drag guard. Nothing else refuses: the day's row
    // count does not change, and the ⋯'s role toggle, which this is the drag of, refuses nothing.
    const source = locateExerciseDay(draft, command.exerciseSlotToken);
    const block = source === null ? null : workBlockOf(source.day, command.exerciseSlotToken);
    if (block === null) return { kind: 'refused', reason: 'unknownTarget' };
    return { kind: 'movedToWarmup', count: block.size };
  }

  const refusal = libraryDropRefusal(draft, command.targetDaySlotToken);
  if (refusal !== null) return { kind: 'refused', reason: refusal };
  const target = locateDaySite(draft, command.targetDaySlotToken);
  if (target === null) return { kind: 'refused', reason: 'unknownTarget' };
  const blockCount = workBlockCount(target.day);
  if (command.role === 'Warmup') {
    // The warm-up run has no positions to land between, so an insert there has no index to report.
    // It is still a real outcome — the trainer must be told the movement arrived.
    return { kind: 'inserted', position: blockCount, total: blockCount };
  }
  const to =
    command.targetBlockIndex === 'end'
      ? blockCount
      : Math.min(Math.max(command.targetBlockIndex, 0), blockCount);
  return { kind: 'inserted', position: to + 1, total: blockCount + 1 };
}

/**
 * Run the command, and answer with the slot token an INSERT minted — `null` for every move, which
 * carries an identity it already had.
 *
 * The caller has already asked `moveOutcome` and got something other than a refusal or a no-op —
 * this does not re-gate, because every operation below already returns quietly on an address it
 * cannot resolve and the two checks would be a rule in two places.
 */
export function applyMove(draft: Draft<PlanDraft>, command: MoveCommand): string | null {
  if (command.kind === 'moveDayToWeekday') {
    moveDayToWeekday(draft, command.daySlotToken, command.targetWeekday);
    return null;
  }
  if (command.kind === 'moveDayToPosition') {
    moveDayToPosition(draft, command.daySlotToken, command.targetIndex);
    return null;
  }
  if (command.kind === 'moveExercise') {
    moveExercise(
      draft,
      command.targetDaySlotToken,
      command.exerciseSlotToken,
      command.targetBlockIndex,
    );
    return null;
  }
  if (command.kind === 'moveExerciseToWarmup') {
    moveExerciseBlockToWarmup(draft, command.exerciseSlotToken);
    return null;
  }
  // Both appends go through `addExercise`, which is the operation that already puts a movement at
  // the end of a block — the warm-up run, which draws no positions to land between, and the main
  // work when the caller said `'end'` rather than naming a gap.
  if (command.role === 'Warmup' || command.targetBlockIndex === 'end') {
    return addExercise(draft, command.targetDaySlotToken, command.pick, command.role);
  }
  return insertExerciseAt(
    draft,
    command.targetDaySlotToken,
    command.pick,
    command.targetBlockIndex,
  );
}
